import { css } from '@emotion/react';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { DateRange, isInRange } from 'shared';

import { theme } from '../../theme';

import { InputField } from './Input';

interface DateInputProps {
  selected?: DateTime;
  title: string;
  selectable?: DateRange;
  showIcon?: boolean;
  onSelect: (date: DateTime) => void;
}

const input = css`
  border-radius: ${theme.borders.radius.md};
  width: 100%;
`;

export function DateInput({ selected, title, selectable, onSelect, showIcon = false }: DateInputProps) {
  const [editableDate, setEditableDate] = useState(false);
  const [dateInputValue, setDateInputValue] = useState<string>(selected?.toISODate() || '');
  const originallySelected = useRef(selected);

  useEffect(() => {
    if (selected) {
      setDateInputValue(selected.toISODate()!);
    }
  }, [selected]);

  const handleDateInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^\d{0,4}-?\d{0,2}-?\d{0,2}$/;

    if (regex.test(value)) {
      setDateInputValue(value);
      if (value.length === 10 || (value.length == 8 && !value.includes('-'))) {
        handleDateInputBlur(e);
      }
    }
  };

  const handleDateInputBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const date = DateTime.fromISO(e.target.value);

    const formatValid = date.isValid;

    if (!formatValid) {
      return onSelect(originallySelected.current || DateTime.now());
    }

    const rangeValid = selectable ? isInRange({ date, range: selectable }) : true;

    if (!rangeValid) {
      return onSelect(originallySelected.current || DateTime.now());
    }

    onSelect(date);
    if (e.target.value === dateInputValue) {
      setEditableDate(false);
    }
  };

  return (
    <InputField
      css={input}
      name={title}
      value={editableDate ? dateInputValue : DateTime.fromISO(dateInputValue).toFormat('ccc dd LLL')}
      label={t(title)}
      onChange={handleDateInputChange}
      onBlur={handleDateInputBlur}
      onFocus={() => setEditableDate(true)}
      iconName={showIcon ? 'cl--calendar' : undefined}
    />
  );
}
